import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { motion } from "framer-motion";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ActivityPicker, AvailabilityEditor } from "@/components/member";
import PayPalSubscribeButton from "@/components/PayPalSubscribeButton";
import MembershipPlans from "@/components/MembershipPlans";
import { Logo, PageLoader } from "@/components/common";
import { Check, ExternalLink } from "lucide-react";

export const COMMUNITIES = {
    Tampa: [27.9506, -82.4572], "St. Petersburg": [27.7676, -82.6403], Clearwater: [27.9659, -82.8001],
    "Wesley Chapel": [28.2397, -82.3276], Brandon: [27.9378, -82.2859], Riverview: [27.8661, -82.3265],
    Lutz: [28.1511, -82.4615], "New Tampa": [28.09, -82.354],
};
const RADII = [5, 10, 15, 25, 50];
const AGE_OPTIONS = [["18-25", "18–25"], ["26-35", "26–35"], ["36-45", "36–45"], ["46-55", "46–55"], ["56-65", "56–65"], ["66+", "66+"], ["any", "I don't mind the age group"]];

export default function Onboarding() {
    const { user, refresh } = useAuth();
    const nav = useNavigate();
    const [step, setStep] = useState(0);
    const [loading, setLoading] = useState(false);
    const [acts, setActs] = useState(null);
    const [community, setCommunity] = useState(user?.city && COMMUNITIES[user.city] ? user.city : "Tampa");
    const [cityText, setCityText] = useState(user?.city || "");
    const [prefs, setPrefs] = useState({});
    const [avail, setAvail] = useState([]);
    const [radius, setRadius] = useState(user?.travel_radius || 25);
    const [agePrefs, setAgePrefs] = useState(user?.age_prefs || []);
    const [membership, setMembership] = useState(null);

    useEffect(() => {
        api.get("/activities").then((r) => setActs(r.data));
        api.get("/me/membership").then((r) => setMembership(r.data));
    }, []);

    if (!acts) return <div className="min-h-screen bg-background"><PageLoader /></div>;

    const togglePref = (a) => {
        setPrefs((p) => {
            const next = { ...p };
            if (!next[a.id]) next[a.id] = "love";
            else if (next[a.id] === "love") next[a.id] = "try";
            else delete next[a.id];
            return next;
        });
    };

    const toggleAge = (v) => setAgePrefs((cur) => {
        if (v === "any") return cur.includes("any") ? [] : ["any"];
        const next = cur.filter((x) => x !== "any");
        return next.includes(v) ? next.filter((x) => x !== v) : [...next, v];
    });

    const steps = ["About You", "What you like", "Availability", "Travel", "Membership"];

    const saveAbout = async () => {
        try {
            const r = await api.get(`/geocode?q=${encodeURIComponent(cityText)}`);
            const label = r.data.state ? `${r.data.city}, ${r.data.state}` : r.data.city;
            await api.put("/me/profile", { city: label, lat: r.data.lat, lon: r.data.lon });
            setStep(1);
        } catch (e) { toast.error("Couldn't find that city — try 'City, State' or a ZIP."); }
    };
    const savePrefs = async () => {
        const preferences = Object.entries(prefs).map(([activity_id, level]) => ({ activity_id, level }));
        if (preferences.length < 3) { toast.error("Pick at least 3 activities you enjoy."); return; }
        await api.put("/me/preferences", { preferences });
        setStep(2);
    };
    const addAvail = async (w) => {
        const { data } = await api.post("/me/availability", w);
        setAvail((a) => [...a, data]);
    };
    const updAvail = async (id, w) => {
        const { data } = await api.put(`/me/availability/${id}`, w);
        setAvail((a) => a.map((x) => (x.id === id ? data : x)));
    };
    const delAvail = async (w) => {
        await api.delete(`/me/availability/${w.id}`);
        setAvail((a) => a.filter((x) => x.id !== w.id));
    };
    const saveTravel = async () => { await api.put("/me/profile", { travel_radius: radius }); setStep(4); };

    const finish = async () => {
        setLoading(true);
        try {
            await api.post("/me/complete-onboarding");
            await refresh();
            toast.success("You're all set! Let's find something to do.");
            nav("/app", { replace: true });
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
        } finally { setLoading(false); }
    };

    const onPaid = async (subId, tier) => {
        await api.post("/me/membership/confirm", { provider_subscription_id: subId, tier });
        const r = await api.get("/me/membership");
        setMembership(r.data);
        toast.success("Membership active. Welcome to the club!");
    };

    return (
        <div className="min-h-screen bg-background">
            <div className="mx-auto max-w-2xl px-5 py-6">
                <div className="flex items-center justify-between">
                    <Logo />
                    <span className="text-sm text-muted-foreground">Step {step + 1} of 5</span>
                </div>
                <Progress value={((step + 1) / 5) * 100} className="mt-4 h-2" />
                <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="mt-8">
                    <p className="text-xs font-700 uppercase tracking-wide text-accent">{steps[step]}</p>

                    {step === 0 && (
                        <div className="mt-2">
                            <h1 className="font-display text-3xl font-700 tracking-tight text-primary">Nice to meet you, {user?.first_name} 👋</h1>
                            <p className="mt-2 text-muted-foreground">Where are you based? Outings are forming all over the USA — enter any US city.</p>
                            <div className="mt-6 max-w-sm">
                                <input data-testid="onb-city" value={cityText} onChange={(e) => setCityText(e.target.value)} placeholder="Your city, state (e.g. Austin, TX) or ZIP"
                                    className="w-full rounded-xl border border-border bg-card px-4 py-3 text-sm outline-none focus:border-primary" />
                            </div>
                            <Button onClick={saveAbout} data-testid="onb-next-0" className="mt-8 rounded-full bg-primary hover:bg-primary/90">Continue</Button>
                        </div>
                    )}

                    {step === 1 && (
                        <div className="mt-2">
                            <h1 className="font-display text-3xl font-700 tracking-tight text-primary">What do you like to do?</h1>
                            <p className="mt-2 text-muted-foreground">Choose everything that sounds fun. You choose the activity — we'll find the people.</p>
                            <div className="mt-6"><ActivityPicker byCategory={acts.by_category} categories={acts.categories} mode="prefs" selected={prefs} onToggle={togglePref} /></div>
                            <div className="mt-8 flex gap-3">
                                <Button variant="outline" onClick={() => setStep(0)} className="rounded-full">Back</Button>
                                <Button onClick={savePrefs} data-testid="onb-next-1" className="rounded-full bg-primary hover:bg-primary/90">Continue</Button>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div className="mt-2">
                            <h1 className="font-display text-3xl font-700 tracking-tight text-primary">When are you free?</h1>
                            <p className="mt-2 text-muted-foreground">Add a few upcoming dates and time windows that work for you.</p>
                            <div className="mt-6"><AvailabilityEditor items={avail} onAdd={addAvail} onUpdate={updAvail} onDelete={delAvail} testidPrefix="onb-avail" /></div>
                            <div className="mt-8 flex gap-3">
                                <Button variant="outline" onClick={() => setStep(1)} className="rounded-full">Back</Button>
                                <Button onClick={() => { if (avail.length === 0) { toast.error("Add at least one time window."); return; } setStep(3); }} data-testid="onb-next-2" className="rounded-full bg-primary hover:bg-primary/90">Continue</Button>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div className="mt-2">
                            <h1 className="font-display text-3xl font-700 tracking-tight text-primary">How far would you travel?</h1>
                            <p className="mt-2 text-muted-foreground">We'll only match you with groups within this distance.</p>
                            <div className="mt-6 flex flex-wrap gap-3">
                                {RADII.map((r) => (
                                    <button key={r} data-testid={`onb-radius-${r}`} onClick={() => setRadius(r)}
                                        className={`rounded-full border px-6 py-3 font-600 transition-colors ${radius === r ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/40"}`}>
                                        {r} miles
                                    </button>
                                ))}
                            </div>
                            <div className="mt-8" data-testid="onb-age-prefs">
                                <p className="text-sm font-600 text-primary">Which age group would you generally prefer to socialise with?</p>
                                <p className="mt-1 text-xs text-muted-foreground">Pick any that feel right — you can choose more than one. It's a preference, not a guarantee; it just helps us build groups that feel comfortable and natural.</p>
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {AGE_OPTIONS.map(([val, label]) => (
                                        <button key={val} type="button" data-testid={`onb-age-${val}`} onClick={() => toggleAge(val)}
                                            className={`rounded-full border px-4 py-2 text-sm font-600 transition-colors ${agePrefs.includes(val) ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/40"}`}>
                                            {label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="mt-8 flex gap-3">
                                <Button variant="outline" onClick={() => setStep(2)} className="rounded-full">Back</Button>
                                <Button onClick={async () => { try { await api.put("/me/age-prefs", { age_prefs: agePrefs }); } catch (e) {} saveTravel(); }} data-testid="onb-next-3" className="rounded-full bg-primary hover:bg-primary/90">Continue</Button>
                            </div>
                        </div>
                    )}

                    {step === 4 && (
                        <div className="mt-2">
                            <h1 className="font-display text-3xl font-700 tracking-tight text-primary">Activate your membership</h1>
                            <p className="mt-2 text-muted-foreground">Choose the plan that fits you. Your first month is FREE. Individual activity costs (food, admission, etc.) are separate and paid directly to venues.</p>
                            <div className="mt-6">
                                {membership?.membership?.status === "active" ? (
                                    <div className="flex items-center gap-2 rounded-2xl bg-card p-6 text-primary shadow-sm" data-testid="onb-membership-active">
                                        <Check className="h-5 w-5" /> <span className="font-600">Your membership is active.</span>
                                    </div>
                                ) : (
                                    <MembershipPlans onPaid={onPaid} />
                                )}
                            </div>
                            <div className="mt-8 flex gap-3">
                                <Button variant="outline" onClick={() => setStep(3)} className="rounded-full">Back</Button>
                                <Button onClick={finish} disabled={loading} data-testid="onb-finish" className="rounded-full bg-primary hover:bg-primary/90">Enter the club</Button>
                            </div>
                        </div>
                    )}
                </motion.div>
            </div>
        </div>
    );
}
